/* Featured servers strip above the server browser. Moved verbatim from
   player/src/App.tsx; the API client is injected like the browser's. The
   `ServerCard` recipe is exported so launcher-only strips (recently joined)
   read as the same family. */
import { memo, useEffect, useMemo, type ReactNode } from 'react'
import { Sparkles } from './Icons'
import { fmt, flag, mapLabel } from './format'
import { StarButton } from './server-browser'
import type { ServerFilters, ServerRow, ServersResp } from './types'

export function ServerCard({ s, onOpen, badge, right, star }: {
  s: ServerRow; onOpen: (id: number) => void
  badge: ReactNode; right?: ReactNode; star?: { on: boolean; onToggle: () => void }
}) {
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(s.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(s.id) } }}
      className="cursor-pointer text-left rounded-xl bg-[var(--background-tertiary)] hover:bg-[var(--surface-1-hover)] transition-colors p-3.5">
      <div className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5">{flag(s.country)}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium truncate">{s.name}</span>
          <span className="mono text-[10px] text-[var(--muted)]">{mapLabel(s.map)} · in-game {s.game_time ?? '—'}</span>
        </span>
        {star && <StarButton on={star.on} onToggle={star.onToggle} />}
      </div>
      <div className="flex items-center justify-between mt-2.5">
        {badge}
        {right ?? <span className="mono text-sm">{fmt(s.players)}/{fmt(s.max_players)}{s.queue ? <span className="text-[var(--accent)]"> +{s.queue}</span> : null}</span>}
      </div>
    </div>
  )
}

export function StripHead({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-2.5">
      {icon}
      <span className="mono text-[11px] uppercase tracking-wider text-[var(--accent)]">{children}</span>
    </div>
  )
}

export const FeaturedStrip = memo(function FeaturedStrip({ favs, onToggleFav, onOpen, api, rows: rowsIn }: {
  favs: number[]; onToggleFav: (id: number) => void; onOpen: (id: number) => void
  api: { servers(f: ServerFilters): Promise<ServersResp> }
  rows?: ServerRow[]
}) {
  const [rows, setRows] = useStateRows(rowsIn)
  useEffect(() => {
    if (rowsIn) return
    api.servers({ featured: true, online: true, sort: 'players', limit: 12 })
      .then((r) => setRows(r.servers)).catch(() => {})
  }, [api, rowsIn, setRows])
  // Hooks must run on every render: this useMemo used to sit below the early
  // return, so the first featured server flipped the hook count and React
  // threw, taking the whole page down.
  const favSet = useMemo(() => new Set(favs), [favs])
  if (rows.length === 0) return null
  return (
    <div className="mb-6">
      <StripHead icon={<Sparkles size={16} className="text-[var(--accent)]" />}>Featured servers</StripHead>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((s) => (
          <ServerCard key={s.id} s={s} onOpen={onOpen}
            star={{ on: favSet.has(s.id), onToggle: () => onToggleFav(s.id) }}
            badge={<span className="mono text-[9px] uppercase tracking-widest text-[var(--accent-foreground)] bg-[var(--accent)] rounded px-1.5 py-0.5">Featured</span>} />
        ))}
      </div>
    </div>
  )
})

import { useState } from 'react'
function useStateRows(initial?: ServerRow[]) {
  return useState<ServerRow[]>(initial ?? [])
}
